/**
 * Three.js 씬/카메라/조명/환경/그림자 (research §10, FR-001).
 * Phase A는 오프라인 동작을 위해 RoomEnvironment를 환경맵으로 사용한다. Phase B에서
 * Poly Haven CC0 HDRI(.hdr)로 교체 가능(T046, public/assets/hdri/).
 */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { LAYOUT } from '../config/layout';
import { buildOffice } from './office';
import { makeWalnutTexture } from './officeTextures';
import { noiseNormalMap, noiseRoughnessMap } from './textures';
import { boxGeo } from './geoUtils';

export class SceneManager {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /** 책상 상판 슬랩 메쉬 — 책상 파괴(DeskBreak) 시 숨기고 반쪽 슬랩으로 대체. */
  deskMesh!: THREE.Mesh;
  /** 데스크 매트 메쉬 — 책상 파괴 시 함께 숨김. */
  deskPadMesh!: THREE.Mesh;
  // 피치를 낮춰(약 18°) 레퍼런스처럼 천장 라인·매입등이 프레임 상단에 걸리게 한다.
  readonly baseCameraPos = new THREE.Vector3(0, 1.72, 3.35);
  private readonly lookAt = new THREE.Vector3(0, 0.62, 0);

  constructor(canvas: HTMLCanvasElement) {
    // PostFX 기본 활성 경로(postfx.ts)에서는 AA를 SMAA 패스가 담당하고 씬은 composer의
    // 비-MSAA 렌더타깃에 그려지므로, 기본 프레임버퍼 MSAA(antialias:true)는 리졸브 대역폭과
    // GPU 메모리만 소모하는 순수 낭비다. `?nofx` 폴백(직접 렌더)일 때만 MSAA를 켠다.
    const nofx = new URLSearchParams(location.search).has('nofx');
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: nofx, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    // 밝은 모던 오픈오피스: 배경/fog를 밝은 데이라이트 톤으로.
    this.scene.background = new THREE.Color(0xdfe7ee);
    this.scene.fog = new THREE.Fog(0xe4eaf0, 14, 42);

    this.camera = new THREE.PerspectiveCamera(54, 1, 0.1, 100);
    this.camera.position.copy(this.baseCameraPos);
    this.camera.lookAt(this.lookAt);

    this.setupEnvironment();
    this.setupLights();
    this.buildStaticMeshes();
    buildOffice(this.scene);
    this.resize();
  }

  private setupEnvironment(): void {
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04);
    this.scene.environment = env.texture;
    // 배경 벽이 순백으로 날아가지 않도록 환경광을 낮게 유지.
    this.scene.environmentIntensity = 0.42;
    pmrem.dispose();
  }

  private setupLights(): void {
    // 자연광 오픈오피스: 스카이 앰비언트는 낮게(과노출 방지) + 우드 플로어 바운스.
    const hemi = new THREE.HemisphereLight(0xe9f1fa, 0x9c8d78, 0.55);
    this.scene.add(hemi);

    // 키 라이트: 우측 대형 창에서 들어오는 웜톤 데이라이트(유일한 그림자 캐스터).
    const key = new THREE.DirectionalLight(0xfff0dd, 2.2);
    key.position.set(6.0, 4.5, 1.5);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 24;
    const s = 5;
    key.shadow.camera.left = -s;
    key.shadow.camera.right = s;
    key.shadow.camera.top = s;
    key.shadow.camera.bottom = -s;
    key.shadow.bias = -0.0003;
    key.shadow.normalBias = 0.02;
    key.shadow.radius = 6; // 소프트 섀도(사진 같은 부드러운 경계)
    this.scene.add(key);

    // 좌측 쿨톤 소프트 필(창 반대편 스카이 바운스, 그림자 없음).
    const fill = new THREE.DirectionalLight(0xe6eefc, 0.32);
    fill.position.set(-3, 3.5, 4);
    this.scene.add(fill);
  }

  private buildStaticMeshes(): void {
    // 책상: 절차 월넛 슬랩 텍스처(저채도 다크 브라운 + 가는 결 스트릭 — 레퍼런스 톤).
    const deskMat = new THREE.MeshStandardMaterial({
      map: makeWalnutTexture(),
      roughness: 0.65,
      metalness: 0.0,
      normalMap: noiseNormalMap(256, 0.35, 4),
      roughnessMap: noiseRoughnessMap(256, 0.65, 0.12, 4),
    });
    const desk = new THREE.Mesh(new THREE.BoxGeometry(...LAYOUT.desk.size), deskMat);
    desk.position.set(0, LAYOUT.desk.topY - LAYOUT.desk.size[1] / 2, 0);
    desk.receiveShadow = true;
    desk.castShadow = true;
    this.scene.add(desk);
    this.deskMesh = desk;

    // 블랙 스틸 프레임 다리(공중부양 방지) — 양측 O자 프레임 + 모디스티 레일 병합.
    this.scene.add(this.buildDeskFrame());

    // 데스크 매트(어두운 무광) — 콤보 히트가 잘 보이도록.
    const matPad = new THREE.Mesh(
      new THREE.BoxGeometry(2.4, 0.012, 1.1),
      new THREE.MeshStandardMaterial({ color: 0x1a1c22, roughness: 0.92 }),
    );
    matPad.position.set(0, LAYOUT.desk.topY + 0.007, 0.05);
    matPad.receiveShadow = true;
    this.scene.add(matPad);
    this.deskPadMesh = matPad;
    // 바닥/벽/천장은 office.ts(오픈오피스 인테리어)가 구성.
  }

  /** 책상 블랙 스틸 프레임: LAYOUT.desk 치수에서 파생(치수 자체는 변경하지 않음). */
  private buildDeskFrame(): THREE.Mesh {
    const [dw, dh, dd] = LAYOUT.desk.size;
    const legTopY = LAYOUT.desk.topY - dh; // 상판 밑면
    const floorTopY = LAYOUT.floor.y + LAYOUT.floor.size[1] / 2; // 물리 바닥 윗면(-1.0)
    const legH = legTopY - floorTopY;
    const legX = dw / 2 - 0.16;
    const legZ = dd / 2 - 0.18;

    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1]) {
      // 양측 O자 프레임: 수직 포스트 2 + 바닥 풋레일 + 상단 레일.
      for (const sz of [-1, 1]) {
        parts.push(boxGeo(0.07, legH, 0.07, sx * legX, floorTopY + legH / 2, sz * legZ));
      }
      parts.push(boxGeo(0.07, 0.05, legZ * 2 + 0.07, sx * legX, floorTopY + 0.025, 0));
      parts.push(boxGeo(0.07, 0.05, legZ * 2 + 0.07, sx * legX, legTopY - 0.025, 0));
    }
    // 후면 모디스티 레일(좌우 프레임 연결).
    parts.push(boxGeo(legX * 2 - 0.07, 0.16, 0.04, 0, legTopY - 0.3, -legZ + 0.02));

    const frame = new THREE.Mesh(
      mergeGeometries(parts),
      new THREE.MeshStandardMaterial({ color: 0x1b1d21, roughness: 0.4, metalness: 0.6 }),
    );
    for (const g of parts) g.dispose();
    frame.castShadow = true;
    frame.receiveShadow = true;
    return frame;
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.camera.aspect = w / h;
    // 모바일 세로 화면에서 책상이 잘리지 않도록 FOV 소폭 확대.
    this.camera.fov = h > w ? 66 : 54;
    this.camera.updateProjectionMatrix();
  }
}
